import { buildClouds, emptyPlacement, emptyView, placeCloud, type CloudSpec } from '../worldmap/cloud-layer';
import { MIN_ZOOM } from '../worldmap/worldmap-renderer';
import { isReduceMotionEnabled } from '../../engine/reduce-motion';

/**
 * A couple of drifting Martian clouds over the colony city view — the same
 * sprites and motion model as the world map, deliberately much quieter: three
 * clouds instead of 140, a fraction of the opacity, and no camera (the colony
 * view has no pan/zoom, so the "map" is simply the host's own rectangle).
 *
 * Own pointer-transparent canvas on top of the city canvas, with its own
 * low-rate loop, so it never forces the (on-demand) city scene to redraw.
 * Positions are a pure function of time SINCE MOUNT (see cloud-layer.ts): every
 * visit opens on the same arrangement, and a hidden tab resumes in the right place.
 */

export const COLONY_CLOUD_COUNT = 3;
/** Multiplies the world map's peak opacities (0.45-0.95): about half of them, so it stays clearly subtler than the map sky. */
export const COLONY_CLOUD_ALPHA_SCALE = 0.55;
/** Colony view is one screen, not ~6 — sizes are fractions of the host width, so the map's small figures are scaled up. */
const SIZE_SCALE = 3;
/** Same reason: a crossing of the host should take minutes, not an hour. */
const SPEED_SCALE = 3;
const SEED = 20261013;
const FRAME_INTERVAL_MS = 33;
/** Where each cloud starts (fractions of the wrap range): spread across the view so there is always something to see on arrival. */
const START_U = [0.22, 0.54, 0.84];
const START_V = [0.3, 0.62, 0.42];

/** Pure: the three clouds' specs. Exported for tests. */
export function buildColonyClouds(): CloudSpec[] {
  return buildClouds(COLONY_CLOUD_COUNT, 3, SEED).map((c, i) => ({
    ...c,
    u: START_U[i % START_U.length],
    v: START_V[i % START_V.length],
    size: c.size * SIZE_SCALE,
    vx: c.vx * SPEED_SCALE,
    vy: c.vy * SPEED_SCALE,
    alpha: c.alpha * COLONY_CLOUD_ALPHA_SCALE,
  }));
}

export interface ColonyCloudsHandle {
  destroy(): void;
}

/**
 * @param container The screen's stable root. The `[data-city-view-host]` inside
 *   it is recreated on every render(), so the canvas re-parents itself into
 *   the current one, exactly as the city canvas does.
 */
export function mountColonyClouds(container: HTMLElement): ColonyCloudsHandle {
  const canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  canvas.className = 'pointer-events-none absolute inset-0 w-full h-full';
  let ctx: CanvasRenderingContext2D | null = null;
  try {
    ctx = canvas.getContext('2d');
  } catch {
    ctx = null;
  }

  const specs = buildColonyClouds();
  const placements = specs.map(() => emptyPlacement());
  const view = emptyView();
  view.zoom = MIN_ZOOM;
  view.maxZoom = MIN_ZOOM + 1; // zoom progress 0: no zoom push or zoom fade
  const images: (HTMLImageElement | null)[] = [null, null, null];

  let disposed = false;
  let frameId = 0;
  let lastDraw = 0;
  const mountedAt = Date.now();

  for (let i = 0; i < images.length; i++) {
    const img = new Image();
    img.onload = () => {
      if (disposed) return;
      images[i] = img;
      if (!frameId) draw(elapsed());
    };
    img.src = `/clouds/cloud-${i + 1}.webp`;
  }

  function elapsed(): number {
    return motionAllowed() ? Date.now() - mountedAt : 0;
  }

  function attach(): HTMLElement | null {
    const host = container.querySelector('[data-city-view-host]') as HTMLElement | null;
    if (host && canvas.parentElement !== host) host.appendChild(canvas);
    return host;
  }

  function motionAllowed(): boolean {
    if (isReduceMotionEnabled()) return false;
    return !(typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /** Decoration only: whatever goes wrong while painting it must never reach the screen it sits on. */
  function draw(nowMs: number): void {
    try {
      paint(nowMs);
    } catch {
      // A broken 2D context just means no clouds.
    }
  }

  function paint(nowMs: number): void {
    const host = attach();
    if (!host || !ctx) return;
    // DPR 1 on purpose: the haze is soft, a full-DPR backing store would only cost fill-rate.
    const w = Math.max(1, host.clientWidth);
    const h = Math.max(1, host.clientHeight);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    view.viewportWidth = view.mapWidth = w;
    view.viewportHeight = view.mapHeight = h;
    ctx.clearRect(0, 0, w, h);
    for (let i = 0; i < specs.length; i++) {
      const img = images[specs[i].sprite];
      if (!img) continue;
      const c = placeCloud(specs[i], nowMs, view, placements[i]);
      if (c.alpha < 0.005) continue;
      const dh = (c.width * img.naturalHeight) / img.naturalWidth;
      ctx.globalAlpha = c.alpha;
      ctx.drawImage(img, c.x - c.width / 2, c.y - dh / 2, c.width, dh);
    }
    ctx.globalAlpha = 1;
  }

  function tick(nowMs: number): void {
    frameId = 0;
    if (disposed || document.hidden) return;
    if (!motionAllowed()) {
      draw(0);
      return;
    }
    if (nowMs - lastDraw >= FRAME_INTERVAL_MS) {
      lastDraw = nowMs;
      draw(elapsed());
    }
    frameId = requestAnimationFrame(tick);
  }

  function start(): void {
    if (frameId || disposed || !ctx || document.hidden) return;
    frameId = requestAnimationFrame(tick);
  }

  function onVisibility(): void {
    if (document.hidden) {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = 0;
    } else {
      start();
    }
  }

  attach();
  document.addEventListener('visibilitychange', onVisibility);
  start();

  return {
    destroy(): void {
      if (disposed) return;
      disposed = true;
      if (frameId) cancelAnimationFrame(frameId);
      frameId = 0;
      document.removeEventListener('visibilitychange', onVisibility);
      canvas.remove();
    },
  };
}
